import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Card, Skeleton, Switch, Tooltip, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import {
  getNotificationPreferences,
  updateNotificationPreference,
} from '../../services/notificationService.js';
import { subscribeWebPush, unsubscribeWebPush } from '../../pwa/webPush';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import api from '../../services/apiService';
import './NotificacoesPreferenciasCard.css';

const { Text } = Typography;

/**
 * Central de Notificações: escolha por assunto e por canal (sino, push, e-mail, Telegram).
 * Substitui o antigo card "Notificações no celular" em Configurações.
 */
export default function NotificacoesPreferenciasCard({ isMobile }) {
  const { message } = App.useApp();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({}); // `${categoria}:${canal}` -> true
  const [pushLoading, setPushLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(Boolean(user?.aceita_web_push));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const payload = await getNotificationPreferences();
      setData(payload);
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível carregar suas preferências de notificação.'));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPushEnabled(Boolean(user?.aceita_web_push));
  }, [user?.aceita_web_push]);

  const grupos = useMemo(() => {
    if (!data) return [];
    const ordem = data.grupos || [];
    const porGrupo = new Map();
    for (const cat of data.categorias) {
      if (!porGrupo.has(cat.grupo)) porGrupo.set(cat.grupo, []);
      porGrupo.get(cat.grupo).push(cat);
    }
    return ordem
      .filter((g) => porGrupo.has(g))
      .map((g) => ({ nome: g, categorias: porGrupo.get(g) }));
  }, [data]);

  const canais = data?.canais || [];
  const telegramVinculado = Boolean(data?.telegram_vinculado);

  const handleToggle = async (categoria, canal, valor) => {
    const chave = `${categoria}:${canal}`;
    setSaving((s) => ({ ...s, [chave]: true }));
    // otimista
    setData((prev) => ({
      ...prev,
      categorias: prev.categorias.map((c) =>
        c.id === categoria ? { ...c, valores: { ...c.valores, [canal]: valor } } : c,
      ),
    }));
    try {
      const payload = await updateNotificationPreference({ categoria, canal, valor });
      setData(payload);
      if (categoria === 'marketing' && canal === 'email' && typeof refreshUser === 'function') {
        refreshUser();
      }
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível salvar. Tente novamente.'));
      load();
    } finally {
      setSaving((s) => {
        const next = { ...s };
        delete next[chave];
        return next;
      });
    }
  };

  const handlePushDevice = async (checked) => {
    setPushLoading(true);
    try {
      if (checked) {
        const result = await subscribeWebPush(api);
        if (!result.ok) {
          const msgs = {
            denied: 'Permissão negada no navegador.',
            not_configured: 'Push ainda não configurado no servidor (VAPID).',
            unsupported: 'Este navegador não suporta Web Push.',
          };
          message.warning(msgs[result.reason] || 'Não foi possível ativar o push.');
          setPushEnabled(false);
          return;
        }
        message.success('Notificações push ativadas neste dispositivo.');
        setPushEnabled(true);
      } else {
        await unsubscribeWebPush(api);
        message.success('Notificações push desativadas neste dispositivo.');
        setPushEnabled(false);
      }
      await refreshUser?.();
      load();
    } catch (err) {
      message.error(getErrorMessage(err, 'Falha ao atualizar preferência de push.'));
    } finally {
      setPushLoading(false);
    }
  };

  const cellHint = (canal) => {
    if (canal === 'push' && !pushEnabled) {
      return 'Ative o push neste dispositivo (acima) para receber.';
    }
    if (canal === 'telegram' && !telegramVinculado) {
      return 'Vincule o Telegram para receber por lá.';
    }
    return null;
  };

  return (
    <Card
      title="Notificações"
      bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
      style={{ marginTop: 24 }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Escolha o que você quer receber e em quais canais. O sino do app mostra tudo que
        estiver ligado aqui; e-mails da sua conta (cobrança, segurança) são sempre enviados.
      </Text>

      <div className="np-device-push">
        <span>
          <BellOutlined style={{ marginRight: 8 }} />
          Push neste dispositivo
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
            No iPhone, o app precisa estar na tela inicial.
          </Text>
        </span>
        <Switch checked={pushEnabled} loading={pushLoading} onChange={handlePushDevice} />
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : !data ? (
        <Alert type="error" showIcon message="Não foi possível carregar as preferências." />
      ) : (
        <>
          {!telegramVinculado && (
            <Alert
              type="info"
              showIcon
              style={{ margin: '4px 0 16px' }}
              message="O canal Telegram fica disponível depois de vincular seu Telegram na aba “Integrações”."
            />
          )}

          <div className="np-table-scroll">
            <table className="np-table">
              <thead>
                <tr>
                  <th className="np-col-assunto">Assunto</th>
                  {canais.map((c) => (
                    <th key={c.id} className="np-col-canal">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grupos.map((grupo) => (
                  <React.Fragment key={grupo.nome}>
                    <tr className="np-grupo-row">
                      <td colSpan={canais.length + 1}>{grupo.nome}</td>
                    </tr>
                    {grupo.categorias.map((cat) => (
                      <tr key={cat.id}>
                        <td className="np-col-assunto">
                          <div className="np-assunto-label">{cat.label}</div>
                          <div className="np-assunto-desc">{cat.descricao}</div>
                        </td>
                        {canais.map((c) => {
                          const bloqueado = Boolean(cat.bloqueado?.[c.id]);
                          const chave = `${cat.id}:${c.id}`;
                          const hint = cellHint(c.id);
                          const sw = (
                            <Switch
                              size="small"
                              checked={Boolean(cat.valores?.[c.id])}
                              disabled={bloqueado || Boolean(saving[chave])}
                              loading={Boolean(saving[chave])}
                              onChange={(v) => handleToggle(cat.id, c.id, v)}
                            />
                          );
                          return (
                            <td key={c.id} className="np-col-canal">
                              {bloqueado ? (
                                <Tooltip title="Sempre enviado (mensagens essenciais da sua conta).">
                                  {sw}
                                </Tooltip>
                              ) : hint ? (
                                <Tooltip title={hint}>{sw}</Tooltip>
                              ) : (
                                sw
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
